// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Waft — the canvas board: a grid of zones over a faint mirrored camera view, with
// glowing fireflies, spiky wasps, particle bursts, a cyan motion-pulse per zone (so the
// player SEES their wave land), and screen shake. Colour is never the only cue —
// fireflies are round glows, wasps are striped diamonds. All meaningful colours are
// pinned ≥3:1 against the worst-case camera-composited background (palette + contrast
// test); the camera is drawn at CAMERA_ALPHA so that bound holds.

import { PALETTE } from './palette';
import { boardGeom, zoneRect, type Grid } from './layout';
import type { GameEvent, GameState } from './game';
import type { SwatSource } from './input';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

export class Board {
  private ctx: CanvasRenderingContext2D;
  private w = 0;
  private h = 0;
  private dpr = 1;
  private grid: Grid = { cols: 2, rows: 3 };
  private source: SwatSource | null = null;
  private particles: Particle[] = [];
  private shake = 0;
  private showKeys = false;
  private t = 0;

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('waft: no 2d canvas context');
    this.ctx = ctx;
  }

  setLayout(grid: Grid): void {
    this.grid = grid;
  }
  setSource(source: SwatSource | null): void {
    this.source = source;
  }
  setShowKeys(v: boolean): void {
    this.showKeys = v;
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return; // ignore a transient 0-size measure
    this.dpr = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
    this.w = rect.width;
    this.h = rect.height;
    this.canvas.width = Math.round(this.w * this.dpr);
    this.canvas.height = Math.round(this.h * this.dpr);
  }

  onEvents(events: GameEvent[], reducedMotion: boolean): void {
    for (const e of events) {
      if (e.t === 'catch') {
        this.burst(e.zone, PALETTE.spark, 12, reducedMotion);
        if (!reducedMotion) this.shake = Math.min(9, this.shake + 1.6 + Math.min(e.combo, 20) * 0.12);
      } else if (e.t === 'sting') {
        this.burst(e.zone, PALETTE.wasp, 14, reducedMotion, true);
        if (!reducedMotion) this.shake = Math.min(14, this.shake + 6);
      }
    }
  }

  private burst(zone: number, color: string, n: number, reducedMotion: boolean, sharp = false): void {
    if (reducedMotion) n = Math.min(4, n);
    const { pad, gap } = boardGeom(this.w, this.h);
    const r = zoneRect(zone, this.grid, this.w, this.h, pad, gap);
    const cx = r.x + r.w / 2;
    const cy = r.y + r.h / 2;
    for (let i = 0; i < n; i++) {
      const a = (Math.PI * 2 * i) / n + Math.random() * 0.5;
      const sp = (sharp ? 0.14 : 0.09) * Math.min(r.w, r.h) * (0.5 + Math.random());
      this.particles.push({
        x: cx,
        y: cy,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        life: 1,
        maxLife: 1,
        size: (sharp ? 2.4 : 3.2) + Math.random() * 2,
        color,
      });
    }
  }

  frame(state: GameState, opts: { dtMs: number; reducedMotion: boolean; energy: readonly number[] }): void {
    const { ctx } = this;
    if (this.w < 2 || this.h < 2) this.resize();
    if (this.w < 2) return;
    const dt = Math.min(60, opts.dtMs);
    this.t += dt;

    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, this.w, this.h);
    ctx.fillStyle = PALETTE.board;
    ctx.fillRect(0, 0, this.w, this.h);

    // screen shake
    if (this.shake > 0.1 && !opts.reducedMotion) {
      const sx = (Math.random() - 0.5) * this.shake;
      const sy = (Math.random() - 0.5) * this.shake;
      ctx.translate(sx, sy);
      this.shake *= 0.86;
    } else {
      this.shake = 0;
    }

    // faint mirrored camera backdrop (bounded to bgWorst by CAMERA_ALPHA)
    this.source?.backdrop?.(ctx, this.w, this.h);
    // darken toward the edges so the grid reads clearly regardless of the scene
    const vg = ctx.createRadialGradient(this.w / 2, this.h / 2, this.h * 0.25, this.w / 2, this.h / 2, this.h * 0.75);
    vg.addColorStop(0, 'rgba(12,16,34,0)');
    vg.addColorStop(1, 'rgba(12,16,34,0.55)');
    ctx.fillStyle = vg;
    ctx.fillRect(-20, -20, this.w + 40, this.h + 40);

    const { pad, gap } = boardGeom(this.w, this.h);
    const zones = this.grid.cols * this.grid.rows;

    // zone cells + motion pulse
    for (let z = 0; z < zones; z++) {
      const r = zoneRect(z, this.grid, this.w, this.h, pad, gap);
      const rad = Math.min(r.w, r.h) * 0.14;
      // resting cell
      ctx.fillStyle = 'rgba(22,35,58,0.32)';
      roundRect(ctx, r.x, r.y, r.w, r.h, rad);
      ctx.fill();
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = 'rgba(111,224,255,0.16)';
      ctx.stroke();
      // motion pulse — cyan glow scaled by energy in this zone
      const e = opts.energy[z] ?? 0;
      if (e > 0.05) {
        const a = Math.min(0.85, e * 2.4);
        ctx.save();
        ctx.lineWidth = 3;
        ctx.strokeStyle = PALETTE.pulse;
        ctx.globalAlpha = a;
        ctx.shadowColor = PALETTE.pulse;
        ctx.shadowBlur = 16 * Math.min(1, e * 3);
        roundRect(ctx, r.x + 1.5, r.y + 1.5, r.w - 3, r.h - 3, rad);
        ctx.stroke();
        ctx.restore();
      }
      if (this.showKeys && z < 9) {
        ctx.fillStyle = 'rgba(159,179,198,0.5)';
        ctx.font = `600 ${Math.round(Math.min(r.w, r.h) * 0.14)}px system-ui, sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.fillText(String(z + 1), r.x + rad * 0.7, r.y + rad * 0.6);
      }
    }

    // motes
    for (let z = 0; z < zones; z++) {
      const m = state.byZone[z];
      if (!m || m.state !== 'alive') continue;
      const r = zoneRect(z, this.grid, this.w, this.h, pad, gap);
      const cx = r.x + r.w / 2;
      const cy = r.y + r.h / 2;
      const life = m.tExpire - m.tSpawn;
      const age = state.clock - m.tSpawn;
      const frac = Math.max(0, Math.min(1, age / life));
      const grow = Math.min(1, age / 150);
      const fade = frac > 0.82 ? Math.max(0, (1 - frac) / 0.18) : 1;
      const bob = opts.reducedMotion ? 0 : Math.sin(this.t * 0.006 + m.id) * Math.min(r.w, r.h) * 0.04;
      const base = Math.min(r.w, r.h) * 0.3;
      const scale = grow * (0.85 + 0.15 * fade);
      if (m.kind === 'fly') this.drawFirefly(cx, cy + bob, base * scale, fade);
      else this.drawWasp(cx, cy + bob, base * scale, fade);
    }

    // particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.x += p.vx * dt * 0.06;
      p.y += p.vy * dt * 0.06;
      p.vy += dt * 0.012;
      p.life -= dt / 620;
      if (p.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }
      ctx.globalAlpha = Math.max(0, p.life);
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size * p.life, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  private drawFirefly(cx: number, cy: number, r: number, fade: number): void {
    const { ctx } = this;
    ctx.save();
    // halo
    const halo = ctx.createRadialGradient(cx, cy, r * 0.2, cx, cy, r * 1.9);
    halo.addColorStop(0, `rgba(255,210,74,${0.5 * fade})`);
    halo.addColorStop(1, 'rgba(255,210,74,0)');
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 1.9, 0, Math.PI * 2);
    ctx.fill();
    // body
    ctx.globalAlpha = fade;
    ctx.fillStyle = PALETTE.fly;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    // core sheen
    ctx.fillStyle = PALETTE.flyCore;
    ctx.beginPath();
    ctx.arc(cx - r * 0.28, cy - r * 0.28, r * 0.42, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawWasp(cx: number, cy: number, r: number, fade: number): void {
    const { ctx } = this;
    ctx.save();
    ctx.globalAlpha = fade;
    // warning halo
    const halo = ctx.createRadialGradient(cx, cy, r * 0.2, cx, cy, r * 1.8);
    halo.addColorStop(0, `rgba(255,107,87,${0.4 * fade})`);
    halo.addColorStop(1, 'rgba(255,107,87,0)');
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 1.8, 0, Math.PI * 2);
    ctx.fill();
    // a spiky diamond body (distinct silhouette from the round firefly)
    const spikes = 8;
    ctx.beginPath();
    for (let i = 0; i < spikes * 2; i++) {
      const ang = (Math.PI * i) / spikes - Math.PI / 2;
      const rad = i % 2 === 0 ? r * 1.15 : r * 0.62;
      const x = cx + Math.cos(ang) * rad;
      const y = cy + Math.sin(ang) * rad;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fillStyle = PALETTE.wasp;
    ctx.fill();
    // dark warning stripes
    ctx.strokeStyle = 'rgba(26,20,5,0.85)';
    ctx.lineWidth = Math.max(1.5, r * 0.16);
    ctx.beginPath();
    ctx.moveTo(cx - r * 0.55, cy - r * 0.18);
    ctx.lineTo(cx + r * 0.55, cy - r * 0.18);
    ctx.moveTo(cx - r * 0.5, cy + r * 0.2);
    ctx.lineTo(cx + r * 0.5, cy + r * 0.2);
    ctx.stroke();
    ctx.restore();
  }

  destroy(): void {
    this.particles = [];
  }
}
